/**
 * Pure, framework-agnostic helpers for the structured FRQ "criteria" rubric.
 *
 * This module is the SINGLE source of the criteria shape + validation rules. It
 * knows nothing about immutable.js, the Flux store, Apollo, or React — callers
 * hand it plain objects and get plain objects back. That keeps the data-access
 * seam (the `useRubricCriteria` hook) swappable: when the Ash GQL rubric CRUD
 * ships, only the hook changes, never this logic.
 *
 * Read-path determinism: `criteriaFromRubric` never mints ids (the hook runs it
 * every render). Only `makeCriterion` generates a new id, on the add path.
 */
import {v4 as uuid} from 'uuid';

import type {FrqRubric, FrqRubricCriterion} from './types';

export const CRITERIA_RUBRIC_TYPE = 'criteria' as const;

/** A rubric value read off the question, already converted to plain JS. */
type PlainRubric = {type?: unknown; criteria?: unknown} | null | undefined;

/**
 * Convert a rubric value to the plain shape the helpers read. `question.getMeta()` hands back an
 * Immutable Map in app code and a plain object in tests, and the two question models (authoring vs
 * QuestionModelV3) disagree besides — hence `unknown` in, plain out.
 */
export function toPlainRubric(rubric: unknown): PlainRubric {
  if (!rubric || typeof rubric !== 'object') {
    return null;
  }
  const maybeImmutable = rubric as {toJS?: () => unknown};
  const plain = typeof maybeImmutable.toJS === 'function' ? maybeImmutable.toJS() : rubric;
  return plain as PlainRubric;
}

/**
 * Extract the criteria list from a question's rubric. Returns `[]` for any
 * non-criteria rubric (e.g. the default `markAsCorrect` on FRQs today), so an
 * FRQ with no structured rubric reads as an empty editor (the fallback state).
 */
export function criteriaFromRubric(rubric: PlainRubric): FrqRubricCriterion[] {
  if (!rubric || rubric.type !== CRITERIA_RUBRIC_TYPE || !Array.isArray(rubric.criteria)) {
    return [];
  }
  return rubric.criteria.map(normalizeCriterion);
}

function normalizeCriterion(raw: unknown): FrqRubricCriterion {
  const c = (raw ?? {}) as Record<string, unknown>;
  return {
    // Preserve the backend id verbatim (schema marks it `ID!`); never regenerate.
    id: typeof c.id === 'string' ? c.id : '',
    title: typeof c.title === 'string' ? c.title : '',
    description: typeof c.description === 'string' ? c.description : undefined,
    maxScore: typeof c.maxScore === 'number' && Number.isFinite(c.maxScore) ? c.maxScore : 0
  };
}

/** Wrap a criteria list into the rubric value persisted on the question. */
export function rubricWithCriteria(criteria: FrqRubricCriterion[]): FrqRubric {
  return {type: CRITERIA_RUBRIC_TYPE, criteria};
}

/** Total possible points = Σ maxScore. */
export function totalPoints(criteria: FrqRubricCriterion[]): number {
  return criteria.reduce((sum, c) => sum + (Number.isFinite(c.maxScore) ? c.maxScore : 0), 0);
}

/** A fresh, blank criterion with a generated id (the only id-minting path). */
export function makeCriterion(): FrqRubricCriterion {
  return {id: uuid(), title: '', description: '', maxScore: 1};
}

export type CriterionErrors = {title?: string; maxScore?: string};

/**
 * Per-criterion validation, keyed by criterion id. A criterion needs a non-empty
 * title and a whole-number maxScore > 0. An empty list yields `{}` (valid) — an FRQ may be
 * saved with no structured rubric and falls back to overall-score-only grading.
 */
export function validateCriteria(criteria: FrqRubricCriterion[]): Record<string, CriterionErrors> {
  const errors: Record<string, CriterionErrors> = {};
  for (const c of criteria) {
    const criterionErrors: CriterionErrors = {};
    if (!c.title.trim()) {
      criterionErrors.title = 'Title is required.';
    }
    if (!Number.isInteger(c.maxScore) || c.maxScore <= 0) {
      criterionErrors.maxScore = 'Max score must be a whole number greater than 0.';
    }
    if (criterionErrors.title || criterionErrors.maxScore) {
      errors[c.id] = criterionErrors;
    }
  }
  return errors;
}

/** Whole-rubric validity for the save gate. Empty (no rubric) is valid. */
export function isRubricValid(criteria: FrqRubricCriterion[]): boolean {
  return Object.keys(validateCriteria(criteria)).length === 0;
}
